import { type ChangeEvent, createContext, type FC, type MouseEvent, type ReactElement, useState } from 'react';
import { usePagination } from '../content/hooks';
import type { AmsPartModel, AmsPartsFilterProps, AmsPartsSortingFields, Order } from '../models';

interface IAmsPartsContext {
  amsPartsUpdate: boolean;
  toggleAmsPartsUpdate: () => void;
  amsParts: AmsPartModel[];
  setAmsParts: (val: AmsPartModel[]) => void;
  page: number;
  setPage: (val: number) => void;
  limit: number;
  setLimit: (val: number) => void;
  count: number;
  setCount: (val: number) => void;
  order: Order;
  setOrder: (val: Order) => void;
  orderBy: AmsPartsSortingFields;
  setOrderBy: (val: AmsPartsSortingFields) => void;
  handlePageChange: (_event: any, newPage: number) => void;
  handleLimitChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleRequestSort: (_event: MouseEvent, property: AmsPartsSortingFields) => void;
  loading: boolean;
  setLoading: (val: boolean) => void;
  filters: AmsPartsFilterProps;
  setFilters: (val: AmsPartsFilterProps) => void;
  resetFilters: () => void;
}

export const AmsPartsContext = createContext<IAmsPartsContext>({} as IAmsPartsContext);

export const AmsPartsProvider: FC<{ children: ReactElement }> = ({ children }) => {
  const [amsPartsUpdate, setAmsPartsUpdate] = useState(false);
  const toggleBarUpdate = (): void => setAmsPartsUpdate(!amsPartsUpdate);

  const {
    page,
    setPage,
    limit,
    setLimit,
    count,
    setCount,
    order,
    setOrder,
    orderBy,
    setOrderBy,
    handlePageChange,
    handleLimitChange,
    handleRequestSort
  } = usePagination<AmsPartsSortingFields>(toggleBarUpdate, 'type', 'asc');

  const [amsParts, setAmsParts] = useState<AmsPartModel[]>([]);
  const [loading, setLoading] = useState(false);

  const emptyFilers: AmsPartsFilterProps = {
    brewer: undefined,
    mainCategory: undefined,
    type: undefined
  };
  const [filters, setFilters] = useState<AmsPartsFilterProps>(emptyFilers);

  const resetFilters = (): void => setFilters(emptyFilers);

  return (
    <AmsPartsContext.Provider
      value={{
        amsPartsUpdate,
        toggleAmsPartsUpdate: toggleBarUpdate,
        amsParts,
        setAmsParts,
        page,
        setPage,
        limit,
        setLimit,
        count,
        setCount,
        order,
        setOrder,
        orderBy,
        setOrderBy,
        handlePageChange,
        handleLimitChange,
        handleRequestSort,
        loading,
        setLoading,
        filters,
        setFilters,
        resetFilters
      }}
    >
      {children}
    </AmsPartsContext.Provider>
  );
};
